import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, MapPin, Pencil, Plus, Store as StoreIcon, Trash2 } from "lucide-react";

import { api, ApiError, type Store } from "../../api";
import { Modal } from "../../components/Modal";
import { useScope } from "../../components/ScopeContext";
import { count } from "../../words";
import { EmptyState, Note, SettingsSection } from "./shared";

type Draft = { existing?: string; display_name: string; location: string };

/** A customer's own locations: where their cameras and IntraScene boxes are. */
export function LocationsSection({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const { scope, selectStore } = useScope();
  const stores = useQuery({ queryKey: ["stores"], queryFn: api.stores.get });
  const boxes = useQuery({ queryKey: ["connectors"], queryFn: api.connectors.list });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Store | null>(null);
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => {
    setError(null);
    qc.invalidateQueries({ queryKey: ["stores"] });
    qc.invalidateQueries({ queryKey: ["connectors"] });
  };
  const fail = (fallback: string) => (e: unknown) =>
    setError(e instanceof ApiError ? e.message : fallback);

  const save = useMutation({
    mutationFn: (d: Draft) =>
      d.existing
        ? api.stores.update(d.existing, { display_name: d.display_name, location: d.location })
        : api.stores.create({ name: "", display_name: d.display_name, location: d.location }),
    onSuccess: () => {
      setDraft(null);
      invalidate();
    },
    onError: fail("Could not save the location."),
  });
  const remove = useMutation({
    mutationFn: (name: string) => api.stores.delete(name),
    onSuccess: () => {
      setConfirmDelete(null);
      invalidate();
    },
    onError: (e: unknown) => {
      setConfirmDelete(null);
      fail("Could not delete the location.")(e);
    },
  });

  const all = stores.data?.stores ?? [];
  const boxesAt = (name: string) => (boxes.data ?? []).filter((b) => b.store === name).length;
  const add = () => setDraft({ display_name: "", location: "" });

  return (
    <SettingsSection
      title="Locations"
      description="The places where your cameras are installed."
      hint={
        <>
          Each location has its own cameras, video recorders and IntraScene box.
          Choosing one in the <b>Select location</b> picker only changes what
          you see.
        </>
      }
      actions={
        canEdit && (
          <button className="bf-button" onClick={add}>
            <Plus size={16} className="mr-2" /> Add location
          </button>
        )
      }
    >
      {error && <Note tone="error">{error}</Note>}

      {stores.isLoading ? (
        <div className="bf-card text-sm text-bf-muted">Loading locations…</div>
      ) : all.length === 0 ? (
        <EmptyState
          icon={StoreIcon}
          title="No locations yet"
          hint="Adding your IntraScene box creates its location for you, or you can add one here first."
          action={
            canEdit && (
              <button className="bf-button" onClick={add}>
                <Plus size={16} className="mr-2" /> Add a location
              </button>
            )
          }
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {all.map((s) => {
            const label = s.display_name || s.name;
            const scoped = scope.kind === "store" && scope.store === s.name;
            const nBoxes = boxesAt(s.name);
            return (
              <article
                key={s.name}
                className={
                  "bf-card flex flex-col gap-3 " +
                  (scoped ? "ring-1 ring-inset ring-brand-purple/40" : "")
                }
              >
                <div className="min-w-0">
                  <h3 className="truncate text-base font-semibold text-white">{label}</h3>
                  {s.location && (
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-bf-muted">
                      <MapPin size={12} className="shrink-0" />
                      <span className="truncate">{s.location}</span>
                    </p>
                  )}
                </div>
                <p className="text-sm text-bf-muted">
                  {count(s.camera_count, "camera", "cameras")} ·{" "}
                  {count(s.nvr_count, "video recorder", "video recorders")} ·{" "}
                  {count(nBoxes, "IntraScene box", "IntraScene boxes")}
                </p>
                <div className="mt-auto flex items-center justify-between gap-2 border-t border-white/5 pt-3">
                  <button
                    type="button"
                    className="flex items-center gap-1.5 text-xs text-bf-muted hover:text-bf-text disabled:opacity-40"
                    onClick={() => selectStore(s.name)}
                    disabled={scoped}
                  >
                    <Eye size={14} /> {scoped ? "Showing this location" : "Show this location"}
                  </button>
                  {canEdit && (
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        className="text-bf-muted hover:text-bf-text"
                        aria-label={`Edit ${label}`}
                        onClick={() =>
                          setDraft({
                            existing: s.name,
                            display_name: label,
                            location: s.location,
                          })
                        }
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        type="button"
                        className="text-bf-muted hover:text-red-400"
                        aria-label={`Delete ${label}`}
                        onClick={() => setConfirmDelete(s)}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {draft && (
        <Modal onClose={() => setDraft(null)} labelledBy="location-draft-title">
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate({
                ...draft,
                display_name: draft.display_name.trim(),
                location: draft.location.trim(),
              });
            }}
          >
            <h3 id="location-draft-title" className="text-lg font-medium text-white">
              {draft.existing ? "Edit location" : "Add a location"}
            </h3>
            <label className="block">
              <span className="mb-1 block text-xs text-bf-muted">Name</span>
              <input
                className="bf-input"
                value={draft.display_name}
                onChange={(e) => setDraft({ ...draft, display_name: e.target.value })}
                placeholder="Main Street shop"
                required
                autoFocus
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs text-bf-muted">Address (optional)</span>
              <input
                className="bf-input"
                value={draft.location}
                onChange={(e) => setDraft({ ...draft, location: e.target.value })}
                placeholder="12 Main Street, Springfield"
              />
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="bf-button bg-white/10 bg-none"
                onClick={() => setDraft(null)}
              >
                Cancel
              </button>
              <button
                className="bf-button disabled:opacity-40"
                type="submit"
                disabled={save.isPending || !draft.display_name.trim()}
              >
                {save.isPending ? "Saving…" : draft.existing ? "Save" : "Add location"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {confirmDelete && (
        <Modal onClose={() => setConfirmDelete(null)} panelClassName="w-full max-w-md">
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white">
              Delete “{confirmDelete.display_name || confirmDelete.name}”?
            </h3>
            <p className="text-sm text-bf-muted">
              {confirmDelete.camera_count > 0 || confirmDelete.nvr_count > 0 || boxesAt(confirmDelete.name) > 0
                ? "Remove its cameras and video recorders, and move its IntraScene box, before deleting the location."
                : "Nothing is set up here yet."}
            </p>
            <div className="flex justify-end gap-2">
              <button
                className="bf-button bg-white/10 bg-none"
                onClick={() => setConfirmDelete(null)}
              >
                Cancel
              </button>
              <button
                className="bf-button bg-red-600 bg-none hover:bg-red-500 disabled:opacity-40"
                disabled={remove.isPending}
                onClick={() => remove.mutate(confirmDelete.name)}
              >
                {remove.isPending ? "Deleting…" : "Delete location"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </SettingsSection>
  );
}
